import { fromPromise } from 'xstate5';

import {
  addNewDeliveryAddress as addNewDeliveryAddressService,
  addNewDeliveryAddressV2,
  getDefaultDeliveryLocation,
  getDeliveryAddressPredictions,
  getDeliveryLocationByPlaceId,
  getDeliveryLocationDetailsByAddress,
  getRadarDeliveryAddressPredictions,
} from '../../../services';
import {
  type AddressDetails,
  type AddressWithoutIds,
  type DeliveryLocationWithoutNearbyStore,
  type Location,
  type PositionCoordinates,
} from '../../../types';

import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export const searchByDeliveryLocationPlaceIdActor = fromPromise(
  async ({ input }: { input: SearchByDeliveryPlaceIdActorInput }) =>
    getDeliveryLocationByPlaceId({
      client: input.client,
      addressDetails: input.addressDetails,
      googlePlaceId: input.googlePlaceId,
      googlePlaceAddress: input.googlePlaceAddress,
    }),
);

export const searchByDeliveryLocationAddressActor = fromPromise(
  async ({ input }: { input: SearchByDeliveryAddressActorInput }) =>
    getDeliveryLocationDetailsByAddress({
      client: input.client,
      address: input.address,
    }),
);

export const getDefaultDeliveryLocationActor = fromPromise(
  async ({ input }: { input: DefaultDeliveryLocationActorInput }) =>
    getDefaultDeliveryLocation({
      client: input.client,
      lastInteractedStoreAddressId: input.lastInteractedStoreAddressId,
    }),
);

export const searchDeliveryAddressesActor = fromPromise(
  async ({ input }: { input: SearchDeliveryAddressesActorInput }) => {
    const { client, deliverySearchString, currentPosition, shouldUseRadarForDelivery } = input;

    const near = currentPosition
      ? `${currentPosition.latitude},${currentPosition.longitude}`
      : undefined;

    if (shouldUseRadarForDelivery) {
      return getRadarDeliveryAddressPredictions(client, deliverySearchString, near);
    }

    return getDeliveryAddressPredictions(client, deliverySearchString);
  },
);

export const addNewDeliveryAddressActor = fromPromise(
  async ({ input }: { input: AddNewDeliveryAddressActorInput }) => {
    if (input.shouldUseRadarForDelivery) {
      return addNewDeliveryAddressV2(input.client, input.deliveryLocation);
    }

    return addNewDeliveryAddressService(input.client, input.deliveryLocation);
  },
);

// ─── Types ───────────────────────────────────────────────────────────────────

export type SearchByDeliveryPlaceIdActorInput = {
  client: Client;
  addressDetails: AddressDetails | undefined;
  googlePlaceId: string | undefined;
  googlePlaceAddress: string | undefined;
};

export type SearchByDeliveryAddressActorInput = {
  client: Client;
  address: AddressDetails | AddressWithoutIds | undefined;
};

export type DefaultDeliveryLocationActorInput = {
  client: Client;
  lastInteractedStoreAddressId: string | undefined;
};

export type SearchDeliveryAddressesActorInput = {
  client: Client;
  deliverySearchString: string;
  currentPosition: PositionCoordinates | undefined;
  shouldUseRadarForDelivery: boolean;
};

export type AddNewDeliveryAddressActorInput = {
  client: Client;
  deliveryLocation: Location | DeliveryLocationWithoutNearbyStore | undefined;
  shouldUseRadarForDelivery: boolean;
};
